import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";

export type MaintenanceOperationStatus = { pending: boolean; error: string | null; executionId: string };

/** Keep an opened form mounted so closing never drops an in-flight result or its inputs. */
export function MaintenanceDialog({ open, onClose, title, children, id }: {
  open: boolean; onClose(): void; title: string; children: ReactNode; id?: string;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const [visited, setVisited] = useState(open);
  const titleId = useId();
  useEffect(() => {
    if (open) setVisited(true);
  }, [open]);
  useEffect(() => {
    const element = dialog.current;
    if (!open || !visited || !element) return;
    const origin = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    element.querySelectorAll<HTMLDetailsElement>("details[data-maintenance-options]").forEach(details => { details.open = false; });
    element.showModal();
    closeButton.current?.focus();
    return () => { element.close(); if (origin?.isConnected) origin.focus(); };
  }, [open, visited]);
  return <dialog style={{ margin: "auto" }} id={id} ref={dialog} aria-labelledby={titleId} hidden={!open}
    onCancel={event => { event.preventDefault(); onClose(); }}
    onInvalidCapture={event => {
      const input = event.target as HTMLElement;
      for (let parent = input.parentElement; parent && parent !== event.currentTarget; parent = parent.parentElement) {
        if (parent instanceof HTMLDetailsElement) parent.open = true;
      }
      input.focus();
    }}
    onKeyDown={event => {
      if (event.key !== "Tab") return;
      const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, [tabindex="0"]'))
        .filter(element => element.getClientRects().length > 0 && getComputedStyle(element).visibility !== "hidden");
      const destination = event.shiftKey && document.activeElement === controls[0] ? controls.at(-1)
        : !event.shiftKey && document.activeElement === controls.at(-1) ? controls[0] : null;
      if (destination) { event.preventDefault(); destination.focus(); }
    }}
    className="m-auto max-h-[90dvh] w-[calc(100%-2rem)] max-w-3xl overflow-y-auto rounded-lg border border-border bg-background p-0 text-foreground shadow-xl backdrop:bg-black/50">
    <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-border bg-background px-5 py-3">
      <h2 id={titleId} className="text-lg font-semibold">{title}</h2>
      <Button ref={closeButton} type="button" variant="outline" size="sm" onClick={onClose}>閉じる</Button>
    </div>
    <div className="space-y-4 p-5">
      <p className="text-sm text-muted-foreground">閉じても、開始した処理は継続します。</p>
      {visited ? children : null}
    </div>
  </dialog>;
}
